import { buildClientDevice } from '@redbrickmedia/duplex.user-identity'
import { GROUP_COOKIE } from '../openfeature/cacheKey'
import { groupAttributes } from '../utils/groupAttributes'
import { isHydratingServerRender } from '../utils/render/hydration'
import { renderedForOtherDevice } from '../utils/render/groupDevice'

const RELOADED_KEY = 'group-device-reloaded'

/**
 * Refuses to hydrate a page that was rendered for the other device.
 *
 * The edge serves a rendered page by the group cookie alone, so a browser that
 * still carries a desktop cookie while it now reports a phone is handed the
 * desktop render, cached, and no server check can see it. Rather than hydrate
 * it into a page of mismatches, this drops the cookie and reloads before
 * anything else runs. The reload carries no group, so it gets the client
 * shell, and that load writes the cookie for the device the browser really is.
 *
 * Runs first and holds the app until the reload lands, so no later plugin,
 * mismatch report or analytics event sees the discarded page.
 */
export default defineNuxtPlugin({
  name: 'group-device',
  setup() {
    if (!isHydratingServerRender()) return
    const { isMobile } = buildClientDevice()
    if (!renderedForOtherDevice(groupAttributes(), isMobile)) {
      try {
        sessionStorage.removeItem(RELOADED_KEY)
      } catch {
        // Storage blocked: nothing was stored either.
      }
      return
    }

    // One reload per tab at most. Should the cookie somehow survive, the page
    // hydrates as it is rather than reloading forever.
    try {
      if (sessionStorage.getItem(RELOADED_KEY)) return
      sessionStorage.setItem(RELOADED_KEY, '1')
    } catch {
      return
    }
    useCookie(GROUP_COOKIE).value = null
    window.location.reload()
    return new Promise<never>(() => {})
  },
})
